import React, { useEffect, useState } from 'react'
import Seo from '../Seo'

const resolveApiBaseUrl = () => {
  const raw = (process.env.REACT_APP_API_BASE_URL || '').trim()
  if (!raw) return ''
  return raw.replace(/\/+$/, '')
}

const parseLocale = value => {
  const locale = String(value || '').toLowerCase()
  if (locale.startsWith('pt')) return 'pt-BR'
  if (locale.startsWith('es')) return 'es'
  return 'en-US'
}

const COPY = {
  'en-US': {
    title: 'Complete your Benjamin activation',
    subtitle:
      'Review your quote details, accept the terms, and continue to secure Stripe checkout.',
    buyer: 'Buyer email',
    organization: 'Organization',
    quote: 'Quote',
    plans: 'Plans',
    terms: 'I agree to the Terms of Use, Privacy Policy, and Author Rights policy.',
    submit: 'Continue to secure checkout',
    loading: 'Loading your checkout link...',
    invalid: 'This checkout link is invalid or expired.',
    support: 'Need help? contact@leanit101.com'
  },
  'pt-BR': {
    title: 'Concluir ativação do Benjamin',
    subtitle:
      'Revise os detalhes da proposta, aceite os termos e siga para o checkout seguro no Stripe.',
    buyer: 'E-mail do comprador',
    organization: 'Organização',
    quote: 'Proposta',
    plans: 'Planos',
    terms:
      'Concordo com os Termos de Uso, Política de Privacidade e Política de Direitos Autorais.',
    submit: 'Continuar para checkout seguro',
    loading: 'Carregando seu link de checkout...',
    invalid: 'Este link de checkout é inválido ou expirou.',
    support: 'Precisa de ajuda? contact@leanit101.com'
  },
  es: {
    title: 'Completar activación de Benjamin',
    subtitle:
      'Revisa los detalles de la propuesta, acepta los términos y continúa al checkout seguro de Stripe.',
    buyer: 'Correo del comprador',
    organization: 'Organización',
    quote: 'Propuesta',
    plans: 'Planes',
    terms:
      'Acepto los Términos de Uso, la Política de Privacidad y la Política de Derechos de Autor.',
    submit: 'Continuar al checkout seguro',
    loading: 'Cargando tu enlace de checkout...',
    invalid: 'Este enlace de checkout es inválido o ya expiró.',
    support: '¿Necesitas ayuda? contact@leanit101.com'
  }
}

const BenjaminCheckoutPage = () => {
  const [intent, setIntent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [accepted, setAccepted] = useState(false)
  const token = new URLSearchParams(window.location.search).get('t') || ''
  const apiBaseUrl = resolveApiBaseUrl()
  const locale = parseLocale(intent?.checkout?.locale)
  const copy = COPY[locale] || COPY['en-US']

  useEffect(() => {
    if (!token || !apiBaseUrl) {
      setLoading(false)
      setError(copy.invalid)
      return
    }
    let active = true
    const run = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/billing/checkout-intent?t=${encodeURIComponent(
            token
          )}`
        )
        const data = await response.json()
        if (!response.ok) {
          throw new Error(data?.error || copy.invalid)
        }
        if (!active) return
        setIntent(data)
      } catch (requestError) {
        if (!active) return
        setError(requestError.message || copy.invalid)
      } finally {
        if (!active) return
        setLoading(false)
      }
    }
    run()
    return () => {
      active = false
    }
  }, [token, apiBaseUrl, copy.invalid])

  const handleSubmit = async event => {
    event.preventDefault()
    if (!accepted) {
      setError('terms_not_accepted')
      return
    }
    if (!token || !apiBaseUrl) return
    setSubmitting(true)
    setError('')
    try {
      const successUrl = `${window.location.origin}/pricing?checkout=success`
      const cancelUrl = `${window.location.origin}/pricing?checkout=cancelled`
      const response = await fetch(`${apiBaseUrl}/api/billing/checkout-session/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          successUrl,
          cancelUrl,
          termsAccepted: true,
          termsVersion: '2026-02-ops-promote',
          locale
        })
      })
      const data = await response.json()
      if (!response.ok || !data?.url) {
        throw new Error(data?.error || 'checkout_failed')
      }
      window.location.href = data.url
    } catch (submitError) {
      setError(submitError.message || 'checkout_failed')
      setSubmitting(false)
    }
  }

  const policyUrls = intent?.policyUrls || {}
  const plans = Array.isArray(intent?.plans) ? intent.plans : []

  return (
    <main className='min-h-screen bg-[#060b1f] px-6 py-10 text-slate-100'>
      <Seo
        title="Benjamin Checkout | Lean it 101"
        description="Private checkout route for Benjamin activation."
        path="/checkout/benjamin"
        noindex
      />
      <div className='mx-auto max-w-3xl rounded-2xl border border-white/10 bg-[#0a1024]/85 p-8 shadow-[0_20px_80px_rgba(0,0,0,0.45)]'>
        <h1 className='text-3xl font-semibold tracking-tight'>{copy.title}</h1>
        <p className='mt-3 text-base text-slate-300'>{copy.subtitle}</p>

        {loading && <p className='mt-8 text-slate-300'>{copy.loading}</p>}

        {!loading && error && (
          <div className='mt-6 rounded-xl border border-rose-400/40 bg-rose-950/30 px-4 py-3 text-sm text-rose-200'>
            {error}
          </div>
        )}

        {!loading && intent && (
          <form className='mt-8 grid gap-6' onSubmit={handleSubmit}>
            <div className='grid gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4'>
              <div className='flex items-center justify-between gap-3 text-sm'>
                <span className='text-slate-400'>{copy.organization}</span>
                <span>{intent.organization?.name || '-'}</span>
              </div>
              <div className='flex items-center justify-between gap-3 text-sm'>
                <span className='text-slate-400'>{copy.buyer}</span>
                <span>{intent.checkout?.buyerEmail || '-'}</span>
              </div>
              {intent.checkout?.quoteLabel && (
                <div className='flex items-center justify-between gap-3 text-sm'>
                  <span className='text-slate-400'>{copy.quote}</span>
                  <span>{intent.checkout.quoteLabel}</span>
                </div>
              )}
            </div>

            <div className='rounded-xl border border-white/10 bg-white/[0.03] p-4'>
              <h2 className='text-sm uppercase tracking-[0.2em] text-slate-400'>
                {copy.plans}
              </h2>
              <div className='mt-3 grid gap-3'>
                {plans.map(plan => (
                  <div
                    key={plan.planCode}
                    className='rounded-lg border border-white/10 bg-[#131a34]/70 px-3 py-2 text-sm'
                  >
                    <div className='font-medium'>{plan.planCode}</div>
                    <div className='mt-1 text-slate-300'>
                      {plan.productType} · {plan.billingCadence} · {plan.currency}{' '}
                      {Number(plan.listPrice || 0).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <label className='inline-flex items-start gap-3 text-sm text-slate-300'>
              <input
                type='checkbox'
                className='mt-1 h-4 w-4 accent-cyan-400'
                checked={accepted}
                onChange={event => setAccepted(Boolean(event.target.checked))}
              />
              <span>
                {copy.terms}{' '}
                {policyUrls.termsOfUse && (
                  <a
                    className='text-cyan-300 hover:text-cyan-200'
                    href={policyUrls.termsOfUse}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Terms
                  </a>
                )}{' '}
                {policyUrls.privacyPolicy && (
                  <a
                    className='text-cyan-300 hover:text-cyan-200'
                    href={policyUrls.privacyPolicy}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Privacy
                  </a>
                )}{' '}
                {policyUrls.authorsRights && (
                  <a
                    className='text-cyan-300 hover:text-cyan-200'
                    href={policyUrls.authorsRights}
                    target='_blank'
                    rel='noreferrer'
                  >
                    Author Rights
                  </a>
                )}
              </span>
            </label>

            <button
              type='submit'
              disabled={submitting}
              className='rounded-full bg-gradient-to-r from-[#c9d33a] via-[#f38a1a] to-[#19a2cf] px-6 py-3 text-sm font-semibold uppercase tracking-[0.2em] text-[#0a1024] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {submitting ? '...' : copy.submit}
            </button>
            <p className='text-xs text-slate-400'>{copy.support}</p>
          </form>
        )}
      </div>
    </main>
  )
}

export default BenjaminCheckoutPage
